import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Pill } from '@/shared/components';
import { toApiError, useApi, type PropertyGroupResponse } from '@/shared/api';
import { AreaCard } from './AreaCard';
import { ObjectLocationMap } from './ObjectLocationMap';
import { saveFailureMessage } from './objectSaveFailure';
import { useMapArea } from './useMapArea';

// Darunter ist ein Klick zu grob für ein Haus.
const PRECISE_ZOOM = 16;

/**
 * Lage des Objekts. Die Position ermittelt der Nachlauf aus der Adresse selbst; findet er genau
 * dieses Haus, gilt die Adresse als geprüft. Sonst löst die Karte „prüfen“: den Pin setzen,
 * zurechtziehen oder bestätigen. Gäste sehen die Position in ihrer Mappe.
 */
export function ObjectLocationCard({
  group,
  canEdit,
}: {
  group: PropertyGroupResponse;
  canEdit: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);
  const [picked, setPicked] = useState<{ lat: number; lng: number; zoom: number } | null>(null);
  // Nach einer Ablehnung steht der Pin sonst weiter dort, wohin er gezogen wurde — und sähe
  // gespeichert aus. Eine neue Karte setzt ihn auf die gespeicherte Position zurück.
  const [mapKey, setMapKey] = useState(0);
  const setCoordinates = useMutation({
    mutationFn: ({ latitude, longitude }: { latitude: number; longitude: number }) =>
      api.propertyGroups.setCoordinates(group.id, latitude, longitude),
    onMutate: () => setFailure(null),
    onSuccess: () => {
      setPicked(null);
      toast.success('Position gespeichert');
    },
    onError: async (error) => {
      setFailure(saveFailureMessage(await toApiError(error)));
      setPicked(null);
      setMapKey((key) => key + 1);
    },
    // Auch die Apartmentseiten: sie schicken ihre Adresse beim Speichern mit.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['property-groups'] }),
        queryClient.invalidateQueries({ queryKey: ['properties'] }),
      ]),
  });
  const { latitude, longitude } = group.address;
  const hasPin = typeof latitude === 'number' && typeof longitude === 'number';
  const pending = !!group.positionLookupPending;
  const placing = !hasPin && canEdit;
  const map = useMapArea(group.address, placing);
  const save = (lat: number, lng: number) => {
    if (!setCoordinates.isPending) setCoordinates.mutate({ latitude: lat, longitude: lng });
  };

  // Solange der Nachlauf sucht, fragt die Seite zwei Minuten lang nach — Pin und „geprüft“
  // erscheinen ohne Neuladen.
  useEffect(() => {
    if (!pending) return;
    let ticks = 0;
    const timer = window.setInterval(() => {
      ticks += 1;
      void queryClient.invalidateQueries({ queryKey: ['property-groups', group.id] });
      if (ticks >= 12) window.clearInterval(timer);
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [pending, group.id, queryClient]);

  return (
    <AreaCard
      title="Lage"
      aside={
        <Pill size="lg">
          <MapPin size={12} aria-hidden />
          {pending
            ? 'Wird gesucht'
            : hasPin
              ? group.needsAddressReview
                ? 'Pin prüfen'
                : 'Pin gesetzt'
              : 'Noch kein Pin'}
        </Pill>
      }
    >
      <p className="text-ink-soft text-sm">{explanation(group, hasPin, pending, canEdit)}</p>
      <div className="mt-3">
        {hasPin ? (
          <ObjectLocationMap
            key={mapKey}
            latitude={latitude}
            longitude={longitude}
            onMoved={canEdit ? save : undefined}
          />
        ) : placing && !map.loading ? (
          <ObjectLocationMap
            key={mapKey}
            area={map.area}
            onPicked={(lat, lng, zoom) => setPicked({ lat, lng, zoom })}
          />
        ) : (
          <p className="border-line/60 text-ink-soft rounded border border-dashed p-6 text-center text-sm">
            {placing ? 'Karte wird geladen …' : 'Noch keine Position hinterlegt.'}
          </p>
        )}
      </div>
      {placing && !map.loading && !map.found && !picked && (
        <p className="text-ink-soft mt-2 text-sm">
          Die Umgebung der Adresse war nicht zu finden – bitte zur Adresse hineinzoomen.
        </p>
      )}
      {placing && picked && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            variant="brand"
            size="sm"
            leftIcon={<Check size={14} aria-hidden />}
            loading={setCoordinates.isPending}
            disabled={picked.zoom < PRECISE_ZOOM}
            onClick={() => save(picked.lat, picked.lng)}
          >
            Position übernehmen
          </Button>
          {picked.zoom < PRECISE_ZOOM && (
            <span className="text-ink-soft text-sm">Zum genauen Setzen näher heranzoomen.</span>
          )}
        </div>
      )}
      {hasPin && group.needsAddressReview && !pending && canEdit && (
        <Button
          variant="brand"
          size="sm"
          className="mt-3"
          leftIcon={<Check size={14} aria-hidden />}
          loading={setCoordinates.isPending}
          onClick={() => save(latitude, longitude)}
        >
          Position bestätigen
        </Button>
      )}
      {failure && (
        <p className="text-sealwax mt-3 text-sm" role="alert">
          {failure}
        </p>
      )}
    </AreaCard>
  );
}

function explanation(
  group: PropertyGroupResponse,
  hasPin: boolean,
  pending: boolean,
  canEdit: boolean,
) {
  if (pending) {
    return canEdit
      ? 'Der Kartendienst sucht die Position gerade – meist binnen einer Minute. Sie können den Pin auch selbst setzen.'
      : 'Der Kartendienst sucht die Position gerade.';
  }
  if (!hasPin) {
    return canEdit
      ? 'Der Kartendienst kennt dieses Haus nicht. Klicken Sie auf der Karte auf das Haus und übernehmen Sie die Position – dann gilt die Adresse als geprüft.'
      : 'Der Kartendienst kennt dieses Haus nicht.';
  }
  if (group.needsAddressReview) {
    return canEdit
      ? 'Der Kartendienst kennt dieses Haus nicht genau. Ziehen Sie den Pin an die richtige Stelle oder bestätigen Sie ihn – dann gilt die Adresse als geprüft.'
      : 'Die Position ist noch nicht bestätigt.';
  }
  return canEdit
    ? 'Stimmt der Punkt nicht, ziehen Sie ihn an die richtige Stelle. Gäste sehen ihn in ihrer Mappe.'
    : 'Diese Position sehen Gäste in ihrer Mappe.';
}
